import React, { useState, useEffect, useRef } from "react";
import { Card, Table, Input, Button, Typography, Space, message, Modal, Tooltip } from "antd";
import GeoToberWallSpecificationContainer from "./GeoToberWallSpecificationContainer";
import GeoToberWallQuants from "./GeoToberWallQuants";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  ColumnConfigModal,
  ConfigSection,
  ConfigField,
} from "../../common/ColumnConfigModal";
import { useTender } from "../../../contexts/TenderContext";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import NumberInput from "../../common/NumberInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import BufferedTextInput from "../../common/BufferedTextInput";
import { isDuplicatePlotNo } from "../../../utils/plotNoValidation";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import { saveGeoToberWallBuildUp } from "../../../utils/wallsBuildUpSave";
import { useQueryClient } from "@tanstack/react-query";
import { GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS } from "../../../hooks/useWallSectionBuildUp";
import { addColumnCodes } from "@/utils/addColumnCodes";
import { GEO_TOBER_WALL_REFERENCES } from "@/utils/geoToberWallReferenceMapping";
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";

const { Title, Text } = Typography;

// Excel column definitions for export/import
const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "Housetype" },
  { key: "geoWallGPCW600", title: "GPCW 600mm (m)" },
  { key: "geoWallRW900", title: "GPCW 900mm (m)" },
  { key: "tobermoreTPCW", title: "TPCW xxxxmm (m)" },
];

// Helper function to format column titles with units (same as CutFillSections)
const formatColumnTitle = (baseTitle: string): React.ReactNode => {
  const unitMatch = baseTitle.match(/\s+\((m[²³]?|[Nn]r|t)\)$/);
  let titleWithoutUnit = baseTitle;
  let unit = "";

  if (unitMatch) {
    unit = unitMatch[1];
    titleWithoutUnit = baseTitle.substring(0, unitMatch.index || baseTitle.length);
  }

  if (!unit) {
    return (
      <Tooltip title={baseTitle}>
        <div className="gw-header-title">{baseTitle}</div>
      </Tooltip>
    );
  }

  return (
    <div className="text-center">
      <Tooltip title={titleWithoutUnit}>
        <div className="gw-header-title">{titleWithoutUnit}</div>
      </Tooltip>
      <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
    </div>
  );
};

const TPCW_KEYS = ["tpcw1", "tpcw2", "tpcw3", "tpcw4", "tpcw5", "tpcw6", "tpcw7", "tpcw8"] as const;

interface GeoToberWallRow {
  key: string;
  plotNo: string;
  housetype: string;
  // GEOWALLS - linear metres per wall type (GPCW columns)
  geoWallGPCW600: number; // GPCW 600mm
  gpcw750: number;      // GPCW 750mm
  geoWallRW900: number; // GPCW 900mm
  gpcw1050: number;     // GPCW 1050mm
  // TOBERMORE WALLS - 8 dynamic TPCW columns
  tpcw1: number;
  tpcw2: number;
  tpcw3: number;
  tpcw4: number;
  tpcw5: number;
  tpcw6: number;
  tpcw7: number;
  tpcw8: number;
}

const GeoToberWall: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();
  const queryClient = useQueryClient();

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<GeoToberWallRow[]>([]);
  const [activeTab, setActiveTab] = useState<string>("geowalls");
  const [tableKey, setTableKey] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedGeoToberWallKeys, setSelectedGeoToberWallKeys] = useState<React.Key[]>([]);
  const [isQuantsModalOpen, setIsQuantsModalOpen] = useState(false);
  // Defers the Geowalls/Tobermore tables' mount by one paint cycle so a
  // tender with a lot of plots doesn't make this step look frozen while the
  // table's render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Re-keyed on `activeTab` since
  // switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the tables render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);

  // TPCW dynamic column headers
  const [tpcwColumnHeaders, setTpcwColumnHeaders] = useState<Record<string, string>>({});
  const [tpcwDimensionValues, setTpcwDimensionValues] = useState<Record<string, string>>({});
  const headersInitialized = useRef(false);
  const [tempTpcwColumnHeaders, setTempTpcwColumnHeaders] = useState<Record<string, string>>({});
  const [tempTpcwDimensionValues, setTempTpcwDimensionValues] = useState<Record<string, string>>({});
  const [configModalVisible, setConfigModalVisible] = useState(false);

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<GeoToberWallRow[]>({
    pageId: "geo-tober-wall",
    onChangeDetected: (changes) => {
    },
  }); */

  // Force table re-render when switching tabs
  React.useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [activeTab]);

  // Excel import/export hook
  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<GeoToberWallRow>({
    filename: "GeoToberWall",
    sheetName: "Geo & Tober Wall",
    columns: excelColumns,
    data: data.filter((row) => row.key !== "totals"), // Exclude totals row from export
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => ({
        ...createEmptyRow(`imported_${Date.now()}_${index}`),
        ...row,
      }));
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        geoToberWallTable: processedData,
      });
    },
    skipValidation: true,
  });

  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];

          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (row.some((cell) => String(cell).includes("Plot") && String(cell).includes("No"))) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];
          const dataRows = jsonData.slice(headerRowIndex + 1);

          const findColumnIndex = (searchTerms: string[]) =>
            headers.findIndex((header: any) => {
              const h = String(header || "").toLowerCase().trim();
              return searchTerms.some((term) => h.includes(term.toLowerCase()));
            });

          const plotNoIdx = findColumnIndex(["plot no", "plotno"]);
          const housetypeIdx = findColumnIndex(["house type", "housetype"]);
          const geoWallGPCW600Idx = findColumnIndex(["gpcw 600", "gpcw600", "600mm"]);
          const geoWallRW900Idx = findColumnIndex(["rw ne 900", "rw 900", "900mm"]);
          const tobermoreTPCWIdx = findColumnIndex(["tpcw", "tobermore tpcw"]);

          if (plotNoIdx === -1) {
            alert("Could not find 'Plot No' column in the Excel file. Please check the header format.");
            return;
          }

          const getVal = (row: any[], idx: number, def: any = 0) =>
            idx !== -1 && row[idx] !== undefined && row[idx] !== null ? row[idx] : def;

          const allImportedRows: GeoToberWallRow[] = dataRows
            .filter((row) => row && row.length > 0 && row.some((cell) => cell !== null && cell !== undefined && cell !== ""))
            .map((row, index) => ({
              key: `imported_${Date.now()}_${index}`,
              plotNo: String(getVal(row, plotNoIdx, "")),
              housetype: String(getVal(row, housetypeIdx, "")),
              geoWallGPCW600: Number(getVal(row, geoWallGPCW600Idx, 0)),
              geoWallRW900: Number(getVal(row, geoWallRW900Idx, 0)),
              tpcw1: Number(getVal(row, tobermoreTPCWIdx, 0)),
              tpcw2: 0,
              tpcw3: 0,
              tpcw4: 0,
              tpcw5: 0,
              tpcw6: 0,
              tpcw7: 0,
              tpcw8: 0,
            }))
            .filter((row) => {
              const p = String(row.plotNo || "").trim();
              const hasPlotNo = p !== "" && p !== "0" && p !== "null" && p !== "undefined";
              const housetypeStr = String(row.housetype || "").trim();
              const hasValidHouseType =
                housetypeStr !== "" &&
                !housetypeStr.toLowerCase().includes("total") &&
                !housetypeStr.toLowerCase().includes("sum") &&
                isNaN(Number(housetypeStr)) && // Not purely numeric
                housetypeStr.match(/[a-zA-Z]/); // Contains letters
              return hasPlotNo && hasValidHouseType;
            });

          if (allImportedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          setData(allImportedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            geoToberWallTable: allImportedRows,
          });
          alert(`Successfully imported ${allImportedRows.length} rows.`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.geoToberWallTable) {
      setData(tender.groundworks.geoToberWallTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.geoToberWallTable]);

  // Load TPCW headers from tender context — runs whenever the context is updated (e.g. after API hydration)
  useEffect(() => {
    if (headersInitialized.current) return;
    const headers = (tender?.groundworks as any)?.geoToberWallColumnHeaders;
    const dimensions = (tender?.groundworks as any)?.geoToberWallDimensionValues;
    const hasHeaders = headers && Object.keys(headers).length > 0;
    const hasDimensions = dimensions && Object.keys(dimensions).length > 0;
    if (hasHeaders || hasDimensions) {
      headersInitialized.current = true;
      if (hasHeaders) setTpcwColumnHeaders(headers);
      if (hasDimensions) setTpcwDimensionValues(dimensions);
    }
  }, [
    (tender?.groundworks as any)?.geoToberWallColumnHeaders,
    (tender?.groundworks as any)?.geoToberWallDimensionValues,
  ]);

  // Persist TPCW headers to tender context when they change (only after initialized to avoid overwriting hydrated data)
  useEffect(() => {
    if (!headersInitialized.current) return;
    updateField("groundworks", {
      ...tender.groundworks,
      geoToberWallColumnHeaders: tpcwColumnHeaders,
      geoToberWallDimensionValues: tpcwDimensionValues,
    } as any);
  }, [tpcwColumnHeaders, tpcwDimensionValues]);

  // Sync temp state when modal opens
  useEffect(() => {
    if (configModalVisible) {
      setTempTpcwColumnHeaders({ ...tpcwColumnHeaders });
      setTempTpcwDimensionValues({ ...tpcwDimensionValues });
    }
  }, [configModalVisible]);

  const getTpcwTitle = (key: string): string => {
    // Return stored column header (from Excel import) exactly as-is.
    if (tpcwColumnHeaders[key]) return tpcwColumnHeaders[key];
    // Fallback: build label from dimension value and normalise decimal mm.
    if (tpcwDimensionValues[key]) return normaliseDecimalMmInLabel(`TPCW ${tpcwDimensionValues[key]}mm`);
    return "TPCW xxxxmm";
  };

  const handleTpcwHeaderChange = (key: string, value: string) => {
    if (!value) {
      setTempTpcwDimensionValues((prev) => { const n = { ...prev }; delete n[key]; return n; });
      setTempTpcwColumnHeaders((prev) => { const n = { ...prev }; delete n[key]; return n; });
    } else {
      setTempTpcwDimensionValues((prev) => ({ ...prev, [key]: value }));
      setTempTpcwColumnHeaders((prev) => ({ ...prev, [key]: `TPCW ${value}mm` }));
    }
  };

  const createEmptyRow = (key?: string): GeoToberWallRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    geoWallGPCW600: 0,
    gpcw750: 0,
    geoWallRW900: 0,
    gpcw1050: 0,
    tpcw1: 0,
    tpcw2: 0,
    tpcw3: 0,
    tpcw4: 0,
    tpcw5: 0,
    tpcw6: 0,
    tpcw7: 0,
    tpcw8: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof GeoToberWallRow,
    value: string | number
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        geoToberWallTable: newData,
      });
      return newData;
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      geoToberWallTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedGeoToberWallKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Geowall & Tobermore Walls Bill Summary — see
      // CutFills.tsx's identical fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        geoToberWallTable: newData,
        ...(newData.length === 0 && { geoWallsExtraItems: {} }),
      });
      setSelectedGeoToberWallKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  // Calculate column totals
  const calculateColumnTotals = (): Record<string, number> => {
    return data.reduce(
      (totals, row) => ({
        geoWallGPCW600: totals.geoWallGPCW600 + (row.geoWallGPCW600 || 0),
        gpcw750: totals.gpcw750 + (row.gpcw750 || 0),
        geoWallRW900: totals.geoWallRW900 + (row.geoWallRW900 || 0),
        gpcw1050: totals.gpcw1050 + (row.gpcw1050 || 0),
        tpcw1: totals.tpcw1 + (row.tpcw1 || 0),
        tpcw2: totals.tpcw2 + (row.tpcw2 || 0),
        tpcw3: totals.tpcw3 + (row.tpcw3 || 0),
        tpcw4: totals.tpcw4 + (row.tpcw4 || 0),
        tpcw5: totals.tpcw5 + (row.tpcw5 || 0),
        tpcw6: totals.tpcw6 + (row.tpcw6 || 0),
        tpcw7: totals.tpcw7 + (row.tpcw7 || 0),
        tpcw8: totals.tpcw8 + (row.tpcw8 || 0),
      }),
      { geoWallGPCW600: 0, gpcw750: 0, geoWallRW900: 0, gpcw1050: 0, tpcw1: 0, tpcw2: 0, tpcw3: 0, tpcw4: 0, tpcw5: 0, tpcw6: 0, tpcw7: 0, tpcw8: 0 } as Record<string, number>
    );
  };

  const totals = calculateColumnTotals();

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      const { version } = await forceSaveBothBoqEndpoints(tender, "Save and continue from Geowall & Tobermore Walls", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      // Match the on-change save path in GeoToberWallSpecificationContainer:
      // always push current state to the dedicated walls API. The previous
      // `version >= 2 → copyWallsBuildUpFromPreviousVersion` branch was
      // overwriting the user's just-edited build-up with v(n-1)'s data on
      // every Save & Continue from v2+ — so the geo/tobermore walls API
      // ended up holding the previous version's specs instead of the edits
      // the debounced on-change save had just written.
      await saveGeoToberWallBuildUp({ tender, itemLibraryData, ohpPercent: groundworksTotals.ohpPercent, prelimsPercent: groundworksTotals.preliminariesPercent, versionOverride: version });

      // Direct API POST above doesn't go through the mutation hook, so the
      // React Query cache for the bill summary's GET stays "fresh" and serves
      // pre-save data. Invalidate it so Groundworks Review's Geowall &
      // Tobermore bill summary refetches the latest specs on next mount.
      await queryClient.invalidateQueries({
        queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(tender.projectName, version),
      });

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/review"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      const { version } = await forceSaveBothBoqEndpoints(tender, "Save and exit from Geowall & Tobermore Walls", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      // Match the on-change save path — always save current state to the
      // walls API. See Save & Continue handler above for the rationale.
      await saveGeoToberWallBuildUp({ tender, itemLibraryData, ohpPercent: groundworksTotals.ohpPercent, prelimsPercent: groundworksTotals.preliminariesPercent, versionOverride: version });

      // Invalidate bill-summary cache for the same reason as Save & Continue
      // (direct API POST bypasses the mutation hook's onSuccess invalidation).
      await queryClient.invalidateQueries({
        queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(tender.projectName, version),
      });

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const columns = [
    {
      title: "Plot",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: GeoToberWallRow) => (
        <BufferedTextInput
          value={text}
          onCommit={(newValue) => {
            if (isDuplicatePlotNo(data, record.key, newValue)) {
              message.error(`Plot No "${newValue}" is already in use.`);
              return;
            }
            handleInputChange(record.key, "plotNo", newValue);
          }}
          placeholder="Plot"
        />
      ),
    },
    {
      title: "Housetype",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: GeoToberWallRow) => (
        <Input
          value={text}
          onChange={(e) =>
            handleInputChange(record.key, "housetype", e.target.value)
          }
          placeholder="Type"
          size="small"
        />
      ),
    },
    // GEOWALLS Category Header
    {
      title: (
        <div
          style={{
            backgroundColor: "#90EE90",
            padding: "8px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          GEOWALLS
        </div>
      ),
      children: [
        {
          title: formatColumnTitle("GPCW 600mm (m)"),
          dataIndex: "geoWallGPCW600",
          key: "geoWallGPCW600",
          width: 140,
          render: (value: number, record: GeoToberWallRow) => (
            <NumberInput
              value={value || 0}
              onChange={(newValue) =>
                handleInputChange(record.key, "geoWallGPCW600", newValue)
              }
              placeholder="0.000"
              decimalPlaces={3}
              size="small"
              style={{ textAlign: "right" }}
            />
          ),
        },
        {
          title: formatColumnTitle("GPCW 750mm (m)"),
          dataIndex: "gpcw750",
          key: "gpcw750",
          width: 140,
          render: (value: number, record: GeoToberWallRow) => (
            <NumberInput
              value={value || 0}
              onChange={(newValue) =>
                handleInputChange(record.key, "gpcw750", newValue)
              }
              placeholder="0.000"
              decimalPlaces={3}
              size="small"
              style={{ textAlign: "right" }}
            />
          ),
        },
        {
          title: formatColumnTitle("GPCW 900mm (m)"),
          dataIndex: "geoWallRW900",
          key: "geoWallRW900",
          width: 140,
          render: (value: number, record: GeoToberWallRow) => (
            <NumberInput
              value={value || 0}
              onChange={(newValue) =>
                handleInputChange(record.key, "geoWallRW900", newValue)
              }
              placeholder="0.000"
              decimalPlaces={3}
              size="small"
              style={{ textAlign: "right" }}
            />
          ),
        },
        {
          title: formatColumnTitle("GPCW 1050mm (m)"),
          dataIndex: "gpcw1050",
          key: "gpcw1050",
          width: 140,
          render: (value: number, record: GeoToberWallRow) => (
            <NumberInput
              value={value || 0}
              onChange={(newValue) =>
                handleInputChange(record.key, "gpcw1050", newValue)
              }
              placeholder="0.000"
              decimalPlaces={3}
              size="small"
              style={{ textAlign: "right" }}
            />
          ),
        },
      ],
    },
    // TOBERMORE WALLS Category Header - 8 dynamic TPCW columns
    {
      title: (
        <div
          style={{
            backgroundColor: "#87CEEB",
            padding: "8px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          TOBERMORE WALLS
        </div>
      ),
      children: TPCW_KEYS.map((key) => ({
        title: formatColumnTitle(`${getTpcwTitle(key)} (m)`),
        dataIndex: key,
        key,
        width: 150,
        render: (value: number, record: GeoToberWallRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, key as keyof GeoToberWallRow, newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            size="small"
            style={{ textAlign: "right" }}
          />
        ),
      })),
    },
    {
      title: "Actions",
      key: "action",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_: any, record: GeoToberWallRow) =>
        record.key === "totals" ? null : (
          <Button
            type="text"
            danger
            icon={
              <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
            }
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
    },
  ];

  const getScrollConfig = () => {
    const totalWidth = columns.reduce((sum: number, col: any) => sum + (col.width || 120), 0);
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;
    return { x: totalWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  return (
    <MainLayout
      title="Geowall & Tobermore Walls"
      workflowType="groundworks"
      currentStep="geo-tober-wall"
      previousStep="retaining-walls"
      nextStep="review"
      showFooter={false}
    >
      <Card
        className="shadow-sm geo-tober-wall-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title and Tabs */}
        <div
          className="geo-tober-wall-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Geowall & Tobermore Walls
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Enter the measurements for GEOWALLS and TOBERMORE WALLS
                  sections. All calculations are performed automatically.
                </Text>
              </div>
              {/* <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => setImportModalVisible(true)}
                style={{
                  color: COLORS.primary,
                  fontWeight: 500
                }}
              >
                Import Take-off
              </Button> */}
            </div>
          </div>

          {/* Tab Buttons */}
          <div
            className="geo-tober-wall-sticky-tabs px-6"
            style={{ paddingTop: "24px", paddingBottom: "0px" }}
          >
            <div
              style={{
                marginBottom: "0px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              <Button
                type={activeTab === "geowalls" ? "primary" : "default"}
                onClick={() => setActiveTab("geowalls")}
                className={
                  activeTab === "geowalls"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "geowalls" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "geowalls" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "geowalls" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "geowalls" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "geowalls" ? 2 : 1,
                }}
              >
                Geowalls
              </Button>
              <Button
                type={activeTab === "tobermore" ? "primary" : "default"}
                onClick={() => setActiveTab("tobermore")}
                className={
                  activeTab === "tobermore"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "tobermore" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "tobermore" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "tobermore" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "tobermore" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "tobermore" ? 2 : 1,
                }}
              >
                Tobermore Walls
              </Button>
              <Button
                type={activeTab === "buildup" ? "primary" : "default"}
                onClick={() => setActiveTab("buildup")}
                className={
                  activeTab === "buildup"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "buildup" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "buildup" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "buildup" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "buildup" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "buildup" ? 2 : 1,
                }}
              >
                Walls Section Build Up
              </Button>
            </div>

            {/* Right side - Delete Selected (data tabs only) */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {(activeTab === "geowalls" || activeTab === "tobermore") &&
                selectedGeoToberWallKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedRows}
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Delete Selected ({selectedGeoToberWallKeys.length})
                  </Button>
                )}
            </div>

            {/* Right side - Configure Headers (tobermore tab only) + Quants button */}
            {/* {activeTab === "tobermore" && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalVisible(true)}
                style={{
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                  height: "32px",
                  padding: "0 16px",
                  fontWeight: 500,
                  fontSize: "14px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                className="configure-headers-btn"
              >
                Configure Headers
              </Button>
            )} */}
            {/*<Button
              onClick={() => setIsQuantsModalOpen(true)}
              style={{
                backgroundColor: "#D0EEFE",
                borderColor: "#D0EEFE",
                color: COLORS.primary,
                height: "38px",
                padding: "0 20px",
                fontWeight: 500,
                fontSize: "14px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              Geo & Tober Wall Quants
            </Button>
            */}
          </div>
        </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 geo-tober-wall-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Geowalls Table */}
          {activeTab === "geowalls" && (
            <div>
              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                columns={addColumnCodes([
                  columns[0], // Plot
                  columns[1], // Housetype
                  ...(columns[2].children || []), // Individual GEOWALLS columns without the grouped header
                  columns[4], // Action
                ], GEO_TOBER_WALL_REFERENCES)}
                dataSource={data}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedGeoToberWallKeys,
                  onChange: setSelectedGeoToberWallKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig()}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName(
                        "geo-tober-wall-sticky-header"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="geo-tober-wall-table"
                summary={() => {
                  if (data.length === 0) return null;
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell
                          index={0}
                          colSpan={3}
                          align="center"
                        >
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        {(columns[2].children || []).map((col: any, idx: number) => (
                          <Table.Summary.Cell
                            key={col.key}
                            index={idx + 3}
                            align="right"
                          >
                            <span
                              style={{
                                color: "#000",
                                textAlign: "right",
                                display: "block",
                                paddingRight: "8px",
                              }}
                            >
                              {(totals[col.dataIndex] || 0).toFixed(3)}
                            </span>
                          </Table.Summary.Cell>
                        ))}
                        <Table.Summary.Cell
                          index={(columns[2].children || []).length + 3}
                        />
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
            </div>
          )}

          {/* Tobermore Walls Table */}
          {activeTab === "tobermore" && (
            <div>
              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                columns={addColumnCodes([
                  columns[0], // Plot
                  columns[1], // Housetype
                  ...(columns[3].children || []), // 8 TPCW columns
                  columns[4], // Action
                ], GEO_TOBER_WALL_REFERENCES)}
                dataSource={data}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedGeoToberWallKeys,
                  onChange: setSelectedGeoToberWallKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig()}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName(
                        "geo-tober-wall-sticky-header"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="geo-tober-wall-table"
                summary={() => {
                  if (data.length === 0) return null;
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        {TPCW_KEYS.map((key, idx) => (
                          <Table.Summary.Cell key={key} index={idx + 3} align="right">
                            <span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>
                              {(totals[key] || 0).toFixed(3)}
                            </span>
                          </Table.Summary.Cell>
                        ))}
                        <Table.Summary.Cell index={TPCW_KEYS.length + 3} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
            </div>
          )}

          {/* Walls Section Build Up — always mounted so its event listener stays active */}
          <div style={{ paddingTop: "16px", display: activeTab === "buildup" ? "block" : "none" }}>
            <GeoToberWallSpecificationContainer />
          </div>

          {/* Add New Row Button - only shown on data entry tabs */}
          {(activeTab === "geowalls" || activeTab === "tobermore") && (
            <div style={{ marginTop: "16px" }}>
              <Button
                type="text"
                icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                onClick={addRow}
                style={{
                  color: COLORS.primary,
                  fontWeight: 500,
                  padding: "4px 8px",
                }}
              >
                Add New Row
              </Button>
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Hidden Excel Import/Export Component */}
      <div style={{ display: "none" }}>
        <ExcelImportExport
          onExport={handleExportExcel}
          onImport={handleImportExcel}
          onDownloadTemplate={handleDownloadTemplate}
          importLoading={importLoading}
          importModalVisible={importModalVisible}
          setImportModalVisible={setImportModalVisible}
          hasData={data.length > 0}
        />
      </div>

      <style>{`
        .geo-tober-wall-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .geo-tober-wall-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .geo-tober-wall-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .geo-tober-wall-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .geo-tober-wall-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .geo-tober-wall-card .ant-card-body {
          overflow: visible !important;
        }

        .geo-tober-wall-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .geo-tober-wall-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
          margin-bottom: -1px;
        }

        .inactive-tab-button {
          border-bottom: 1px solid #d9d9d9 !important;
        }

        .geo-tober-wall-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .geo-tober-wall-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .geo-tober-wall-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .geo-tober-wall-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: 1px solid #e5e7eb !important;
        }

        .geo-tober-wall-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .geo-tober-wall-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }


        .geo-tober-wall-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .geo-tober-wall-table .ant-table-thead > tr > th.ant-table-selection-column,
        .geo-tober-wall-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .geo-tober-wall-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .geo-tober-wall-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .geo-tober-wall-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .geo-tober-wall-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .geo-tober-wall-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .geo-tober-wall-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude first 2 columns: Plot No, Housetype) */
        .geo-tober-wall-table td:not(:nth-child(1)):not(:nth-child(2)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .geo-tober-wall-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .geo-tober-wall-table .ant-input-number {
          width: 100%;
        }

        .geo-tober-wall-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .geo-tober-wall-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .geo-tober-wall-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .geo-tober-wall-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .geo-tober-wall-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* TPCW Configure Headers Modal */}
      <ColumnConfigModal
        title="Configure TPCW Column Headers"
        open={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        onApply={() => {
          headersInitialized.current = true;
          setTpcwColumnHeaders(tempTpcwColumnHeaders);
          setTpcwDimensionValues(tempTpcwDimensionValues);
          setConfigModalVisible(false);
        }}
        width={500}
        isViewMode={tender.isViewMode}
      >
        <ConfigSection title="TPCW Columns">
          {TPCW_KEYS.map((key, idx) => (
            <ConfigField key={key} label={`TPCW Column ${idx + 1}`}>
              <Input
                value={tempTpcwDimensionValues[key] || ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Allow only digits and a single decimal point — reject alphabets/symbols.
                  let sanitized = raw.replace(/[^0-9.]/g, "");
                  const firstDot = sanitized.indexOf(".");
                  if (firstDot !== -1) {
                    sanitized =
                      sanitized.slice(0, firstDot + 1) +
                      sanitized.slice(firstDot + 1).replace(/\./g, "");
                  }
                  handleTpcwHeaderChange(key, sanitized);
                }}
                onKeyDown={(e) => {
                  // Block alphabetic keys outright so the field feels like a true number input.
                  if (e.key.length === 1 && !/[0-9.]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                  }
                }}
                onFocus={(e) => e.target.select()}
                placeholder="Enter dimension (e.g. 450)"
                inputMode="decimal"
                suffix="mm"
              />
            </ConfigField>
          ))}
        </ConfigSection>
      </ColumnConfigModal>

      {/* Geo & Tober Wall Quants Modal */}
      <Modal
        title="Geo & Tober Wall Quants - Summary"
        open={isQuantsModalOpen}
        onCancel={() => setIsQuantsModalOpen(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        zIndex={1050}
      >
        <GeoToberWallQuants />
      </Modal>
    </MainLayout>
  );
};

export default GeoToberWall;
